// @flow

import React, { type Element, useEffect, useState } from 'react'
import {
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@material-ui/core'
import { FormHelperText } from '@material-ui/core'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { capitalizeFirstLetter } from '../../../lib/utils'

type Props = {
  fieldName: string,
  value: string,
  setValue: (string) => void,
  setIsValid: (boolean) => void,
  serverError: string,
  removeServerError?: () => void,
  validationFunction?: (string) => string,
}

function BasicPasswordField({
  fieldName,
  value,
  setValue,
  setIsValid,
  serverError,
  removeServerError,
  validationFunction,
}: Props): Element<typeof React.Fragment> {
  const [isShowPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setError(capitalizeFirstLetter(serverError))
  }, [serverError])

  useEffect(() => {
    if (!value) setIsValid(false)
  }, [value, setIsValid])

  const handleChange = (event: SyntheticInputEvent<HTMLInputElement>): void => {
    const value = event.target.value
    if (value === '') {
      setError('Cannot be empty')
      setIsValid(false)
    } else if (validationFunction) {
      let error = validationFunction(value)
      setError(error)
      setIsValid(!error)
    } else {
      setError('')
      if (removeServerError) removeServerError()
      setIsValid(true)
    }
    setValue(value)
  }

  const handleClickShowPassword = () => {
    setShowPassword((currentValue) => !currentValue)
  }

  const handleMouseDownPassword = (event: SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const parameterizedFieldName = fieldName.toLowerCase().replace(/\s/g, '-')

  return (
    <FormControl variant="outlined" fullWidth>
      <InputLabel htmlFor={parameterizedFieldName} variant="outlined" required error={error !== ''}>
        {fieldName}
      </InputLabel>
      <OutlinedInput
        id={parameterizedFieldName}
        type={isShowPassword ? 'text' : 'password'}
        value={value}
        onChange={handleChange}
        fullWidth
        required
        error={error !== ''}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {isShowPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        }
        label={fieldName}
      />
      <FormHelperText error={true}>{error}</FormHelperText>
    </FormControl>
  )
}

export default BasicPasswordField
